import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Phalanx — Space Invaders canon: formation of aliens marches L/R,
 * drops on edge-touch, fires bombs probabilistically; ship at bottom
 * moves L/R and fires upward. Game 20 of platform games + 6-game
 * rebrand pack (MVP / v0).
 *
 * Design dossier: docs/dossier-games/20-phalanx/
 * Source IP: Taito Space Invaders (1978).
 *
 * MVP scope:
 *   - One 8×4 formation for the run (32 aliens)
 *   - Formation steps speed up linearly as aliens die
 *   - Bombs fired deterministically from a seeded RNG against
 *     formation state (frontmost alive alien in a random column)
 *   - Ship: ←/→ to steer, Space to fire (FIRE_COOLDOWN_MS)
 *   - Up-projectile hits alien → +row_weight; bomb hits ship → death
 *   - +CLEAR_BONUS on formation cleared (no respawn at MVP)
 *   - 120 s run cap
 *
 * Deferred to v2 polish:
 *   - UFO/saucer with bucket-timed bonus
 *   - Multi-wave progression (after clear, spawn next wave)
 *   - Barriers
 *   - InstancedMesh alien pool
 *   - Post-processing
 */

// ============================================================
// Constants — must stay byte-identical with scoring-phalanx.ts
// ============================================================

export const RUN_MS = 120_000;
export const SAMPLE_INTERVAL_MS = 33;
export const MAX_INPUT_SAMPLES = 3800;

export const ARENA_HALF_X = 14.0;
export const SHIP_Y = -10.0;
export const SHIP_MAX_SPEED = 22.0;

export const FORMATION_COLS = 8;
export const FORMATION_ROWS = 4;
export const FORMATION_X_SPACING = 2.4;
export const FORMATION_Y_SPACING = 1.8;
export const FORMATION_TOP_Y = 8.0;

export const STEP_INTERVAL_MS_BASE = 400;
export const STEP_INTERVAL_MS_MIN = 100;
export const SHIFT = 0.8;
export const DROP = 1.5;

export const HIT_RADIUS = 0.9;
export const UP_PROJECTILE_SPEED = 28.0;
export const BOMB_SPEED = 12.0;
export const BOMB_FIRE_PROB_PER_TICK = 0.012;
export const BOMB_FIRE_PROB_END = 0.024;

export const FIRE_COOLDOWN_MS = 360;
export const PROJ_TTL_MS = 1500;
export const CLEAR_BONUS = 1500;
export const UFO_FIRST_SPAWN_MS = 15_000;
export const UFO_SPAWN_INTERVAL_MS = 18_000;
export const UFO_SPEED = 20.0;
export const UFO_Y = 11.0;
export const UFO_HIT_RADIUS = 1.4;
export const UFO_BONUS = 500;
export const UFO_PERFECT_MS = 800;
export const UFO_PERFECT_BONUS = 1000;
export const BARRIER_COUNT = 3;
export const BARRIER_HP = 3;
export const BARRIER_Y = -4.5;
export const BARRIER_HALF_X = 1.4;
export const BARRIER_HALF_Y = 0.5;
export const BARRIER_X_SPACING = 7.5;
export function barrierCenterX(idx: number): number {
  return (idx - (BARRIER_COUNT - 1) / 2) * BARRIER_X_SPACING;
}
export const ROW_WEIGHT: number[] = [10, 15, 20, 30];
export const SHIP_HIT_RADIUS = 0.7;
export const BOMB_HIT_RADIUS = 0.4;

// ============================================================
// RNG
// ============================================================

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ============================================================
// Types
// ============================================================

export interface InputSample { t: number; steer: -1 | 0 | 1; fire: 0 | 1 }

export interface PhalanxResult {
  kills: number;
  waves_cleared: number;
  saucer_hits: number;
  saucer_perfects: number;
  barriers_remaining: number;
  died: boolean;
  duration_ms: number;
  inputs: InputSample[];
  scoreEstimate: number;
}

export interface PhalanxCallbacks {
  onHud: (runMs: number, kills: number, alive: number, scoreEstimate: number) => void;
  onGameOver: (result: PhalanxResult) => void;
}

// ============================================================
// Game class
// ============================================================

interface AlienView { mesh: THREE.Mesh; alive: boolean }
interface BombView { x: number; y: number; mesh: THREE.Mesh; spawn_t: number }
interface UpView { x: number; y: number; mesh: THREE.Mesh; spawn_t: number }

export class Phalanx {
  private engine: Engine;
  private audio = new AudioBed();
  private rng: () => number;

  // Ship
  private sx = 0;
  private shipMesh!: THREE.Mesh;

  // Formation
  private formationGroup: THREE.Group;
  private aliens: AlienView[] = [];
  private aliveFlags: Uint8Array;
  private fx = 0;
  private fy = 0;
  private dir: 1 | -1 = 1;
  private lastStepT = 0;
  private initialAlive: number;

  // Projectiles
  private ups: UpView[] = [];
  private bombs: BombView[] = [];
  private lastFireT = -Infinity;

  // v5: UFO/saucer
  private saucerMesh: THREE.Mesh | null = null;
  private saucerSpawnT = -1;        // current saucer's spawn_t (-1 = none)
  private killedSaucerTs = new Set<number>();
  private saucerHits = 0;
  private saucerPerfects = 0;

  // Phase 2: destructible barriers
  private barrierHp: number[] = new Array(BARRIER_COUNT).fill(BARRIER_HP);
  private barrierMeshes: THREE.Mesh[] = [];

  // v6 — three parallax starfield layers (near / mid / far) so the
  // formation reads as moving over depth, not as a 2D HTML5 sprite.
  // Per memory feedback-sibling-games-shallow: the rebrand pack looked
  // 2D-on-3D before this; the floor is real depth cues + post-processing.
  private starLayers: THREE.Points[] = [];

  // Materials
  private bombMat!: THREE.MeshBasicMaterial;
  private upMat!: THREE.MeshBasicMaterial;
  private bombGeo!: THREE.SphereGeometry;
  private upGeo!: THREE.SphereGeometry;

  // Input
  private inLeft = false;
  private inRight = false;
  private fireQueued = false;
  // v5.5 mobile: touch-derived target ship X (in arena coords). When non-
  // null, per-frame logic translates target vs current sx into the same
  // inLeft/inRight flags the keyboard would set, so the rest of the input
  // pipeline (record, sample, steer integration) is unchanged.
  private touchTargetX: number | null = null;

  // Recording
  private inputs: InputSample[] = [];
  private sampleTimer = 0;
  private startedAt = 0;
  private kills = 0;
  private wavesCleared = 0;
  private scoreEstimate = 0;
  private gameOver = false;
  private cb: PhalanxCallbacks;

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "ArrowLeft" || e.code === "KeyA") this.inLeft = true;
    if (e.code === "ArrowRight" || e.code === "KeyD") this.inRight = true;
    if (e.code === "Space") { e.preventDefault(); this.fireQueued = true; }
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code === "ArrowLeft" || e.code === "KeyA") this.inLeft = false;
    if (e.code === "ArrowRight" || e.code === "KeyD") this.inRight = false;
  };

  constructor(canvas: HTMLCanvasElement, cb: PhalanxCallbacks, seed: number) {
    this.cb = cb;
    this.rng = makeRng(seed ^ 0x50484c58);
    this.aliveFlags = new Uint8Array(FORMATION_COLS * FORMATION_ROWS).fill(1);
    this.initialAlive = FORMATION_COLS * FORMATION_ROWS;

    this.engine = new Engine(canvas, { onFrame: (dt) => this.frame(dt) });
    this.engine.scene.background = new THREE.Color(0x010208);
    this.engine.scene.fog = new THREE.Fog(0x010208, 16, 60);
    this.engine.scene.add(new THREE.AmbientLight(0x40506e, 0.55));
    const overhead = new THREE.DirectionalLight(0xfff0e0, 0.7);
    overhead.position.set(0, 18, 6);
    this.engine.scene.add(overhead);

    // Top-down camera (looking at the XY plane from +Z).
    this.engine.camera.position.set(0, 0, 28);
    this.engine.camera.lookAt(0, 0, 0);
    this.engine.camera.fov = 60;
    this.engine.camera.near = 0.1;
    this.engine.camera.far = 100;
    this.engine.camera.updateProjectionMatrix();

    // Border line.
    const borderGeo = new THREE.EdgesGeometry(new THREE.PlaneGeometry(ARENA_HALF_X * 2, 22));
    const borderMat = new THREE.LineBasicMaterial({ color: 0x2a3060 });
    this.engine.scene.add(new THREE.LineSegments(borderGeo, borderMat));

    // Ship.
    const shipGeo = new THREE.BoxGeometry(1.4, 0.5, 0.4);
    const shipMat = new THREE.MeshStandardMaterial({
      color: 0x66e0ff, emissive: 0x44a0e0, emissiveIntensity: 0.6,
      roughness: 0.4, metalness: 0.3,
    });
    this.shipMesh = new THREE.Mesh(shipGeo, shipMat);
    this.shipMesh.position.set(0, SHIP_Y, 0);
    this.engine.scene.add(this.shipMesh);

    // Formation aliens.
    this.formationGroup = new THREE.Group();
    this.engine.scene.add(this.formationGroup);
    const alienGeo = new THREE.IcosahedronGeometry(0.7, 0);
    for (let r = 0; r < FORMATION_ROWS; r++) {
      // Row color shifts back→front (back row = warmest, biggest score).
      const hue = 0.0 + (r / FORMATION_ROWS) * 0.5;
      const c = new THREE.Color().setHSL(hue, 0.8, 0.55);
      const mat = new THREE.MeshStandardMaterial({
        color: c, emissive: c, emissiveIntensity: 0.55,
        roughness: 0.45, metalness: 0.3,
      });
      for (let col = 0; col < FORMATION_COLS; col++) {
        const m = new THREE.Mesh(alienGeo, mat);
        const cx = (col - (FORMATION_COLS - 1) / 2) * FORMATION_X_SPACING;
        const cy = FORMATION_TOP_Y - r * FORMATION_Y_SPACING;
        // v6 — Z-stagger by row so the formation reads as a wedge with
        // depth, not a flat 2D grid. Back row (r=0) sits ~0.9u behind
        // the front row; the difference is small enough that hitboxes
        // (XY-only) stay identical and parity with the server holds.
        const cz = -r * 0.3;
        m.position.set(cx, cy, cz);
        this.formationGroup.add(m);
        this.aliens.push({ mesh: m, alive: true });
      }
    }

    // Phase 2: barrier meshes. Concrete-colored cubes between ship
    // and formation. Visibility tracks barrierHp via fade-on-hit:
    // hp = 3 → opaque, hp = 2 → 75% opacity, hp = 1 → 45%, hp = 0 → hidden.
    const barrierGeo = new THREE.BoxGeometry(BARRIER_HALF_X * 2, BARRIER_HALF_Y * 2, 0.4);
    for (let i = 0; i < BARRIER_COUNT; i++) {
      const mat = new THREE.MeshStandardMaterial({
        color: 0x808880, emissive: 0x3a4040, emissiveIntensity: 0.3,
        roughness: 0.85, metalness: 0.1, transparent: true, opacity: 1.0,
      });
      const m = new THREE.Mesh(barrierGeo, mat);
      m.position.set(barrierCenterX(i), BARRIER_Y, 0);
      this.engine.scene.add(m);
      this.barrierMeshes.push(m);
    }

    // Projectile assets.
    this.bombMat = new THREE.MeshBasicMaterial({ color: 0xff6480 });
    this.bombGeo = new THREE.SphereGeometry(0.18, 8, 6);
    this.upMat = new THREE.MeshBasicMaterial({ color: 0xfff080 });
    this.upGeo = new THREE.SphereGeometry(0.14, 8, 6);

    // v6 — parallax starfield. Three THREE.Points layers at different Z
    // depths and densities give the play space genuine 3D distance:
    //   far  (z=-180): 600 dim stars, slowest visual drift on resize
    //   mid  (z= -90):  300 mid stars
    //   near (z= -45):  120 bright stars + slight color variation
    // Stars don't move per-frame (no extra GPU cost beyond initial draw)
    // — depth is conveyed by Z position relative to the fog (FogExp2
    // is configured for 16-60u, so the far layer is way past it and
    // reads as faint, near is fully visible). Per memory:
    // feedback-sibling-games-shallow §"real 3D scene with depth cues".
    const makeStars = (count: number, z: number, radius: number, sizes: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      // Seeded deterministically off the run seed so the same star pattern
      // is reproducible per-run (matches the seed → run philosophy elsewhere).
      let s = (seed ^ Math.imul(z | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * radius * 2;
        positions[i * 3 + 1] = (r() - 0.5) * radius * 2;
        positions[i * 3 + 2] = z + (r() - 0.5) * 8;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: sizes.color, size: sizes.size, sizeAttenuation: true,
        transparent: true, opacity: 0.85, depthWrite: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(600, -180, 240, { size: 0.6, color: 0x8a92b8 });   // far — cool blue/gray
    makeStars(300,  -90, 140, { size: 0.5, color: 0xaab0d4 });   // mid — slightly brighter
    makeStars(120,  -45,  90, { size: 0.45, color: 0xffe8c8 });  // near — warm tint for variety

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    this.mountTouch();
  }

  /** Mobile: top half tap = fire; bottom half drag = steer.
   *  The bottom-zone horizontal touch X is scaled from screen coords to
   *  arena coords and stored as touchTargetX; per-frame the ship's
   *  inLeft/inRight flags are set from sign(target - sx) so the keyboard
   *  steering pipeline drives the rest of the integration unchanged. */
  private touchFireEl: HTMLElement | null = null;
  private touchSteerEl: HTMLElement | null = null;
  private steerTouchId: number | null = null;
  private onTouchFire = (e: PointerEvent) => { e.preventDefault(); this.fireQueued = true; };
  private updateSteerFromPointer = (e: PointerEvent) => {
    if (!this.touchSteerEl) return;
    const r = this.touchSteerEl.getBoundingClientRect();
    const frac = (e.clientX - r.left) / Math.max(1, r.width);  // 0..1
    const clamped = Math.max(0, Math.min(1, frac));
    // Map [0,1] → arena X with the same clamp the ship uses.
    this.touchTargetX = (clamped * 2 - 1) * (ARENA_HALF_X - 0.7);
  };
  private onTouchSteerDown = (e: PointerEvent) => {
    e.preventDefault();
    this.steerTouchId = e.pointerId;
    if (this.touchSteerEl) this.touchSteerEl.setPointerCapture(e.pointerId);
    this.updateSteerFromPointer(e);
  };
  private onTouchSteerMove = (e: PointerEvent) => {
    if (this.steerTouchId !== e.pointerId) return;
    e.preventDefault();
    this.updateSteerFromPointer(e);
  };
  private onTouchSteerUp = (e: PointerEvent) => {
    if (this.steerTouchId !== e.pointerId) return;
    if (this.touchSteerEl) {
      try { this.touchSteerEl.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    }
    this.steerTouchId = null;
    this.touchTargetX = null;
    this.inLeft = false;
    this.inRight = false;
  };
  private mountTouch() {
    this.touchFireEl = document.getElementById("phalanx-touch-fire");
    this.touchSteerEl = document.getElementById("phalanx-touch-steer");
    if (this.touchFireEl) this.touchFireEl.addEventListener("pointerdown", this.onTouchFire);
    if (this.touchSteerEl) {
      this.touchSteerEl.addEventListener("pointerdown", this.onTouchSteerDown);
      this.touchSteerEl.addEventListener("pointermove", this.onTouchSteerMove);
      this.touchSteerEl.addEventListener("pointerup", this.onTouchSteerUp);
      this.touchSteerEl.addEventListener("pointercancel", this.onTouchSteerUp);
    }
  }
  private unmountTouch() {
    if (this.touchFireEl) this.touchFireEl.removeEventListener("pointerdown", this.onTouchFire);
    if (this.touchSteerEl) {
      this.touchSteerEl.removeEventListener("pointerdown", this.onTouchSteerDown);
      this.touchSteerEl.removeEventListener("pointermove", this.onTouchSteerMove);
      this.touchSteerEl.removeEventListener("pointerup", this.onTouchSteerUp);
      this.touchSteerEl.removeEventListener("pointercancel", this.onTouchSteerUp);
    }
  }

  /** v5: deterministic saucer schedule. Same formula as server. */
  private saucerAt(t_ms: number): { spawn_t: number; x: number } | null {
    if (t_ms < UFO_FIRST_SPAWN_MS) return null;
    const sinceFirst = t_ms - UFO_FIRST_SPAWN_MS;
    const cycle = sinceFirst % UFO_SPAWN_INTERVAL_MS;
    const spawn_t = t_ms - cycle;
    const elapsed_s = cycle / 1000;
    const crossingSpan = (ARENA_HALF_X * 2 + 4) / UFO_SPEED;
    if (elapsed_s > crossingSpan) return null;
    const x = -ARENA_HALF_X - 2 + UFO_SPEED * elapsed_s;
    return { spawn_t, x };
  }

  /** Phase 2: AABB-test a point (projectile center) against a barrier. */
  private barrierHits(px: number, py: number, idx: number): boolean {
    const cx = barrierCenterX(idx);
    return Math.abs(px - cx) < BARRIER_HALF_X && Math.abs(py - BARRIER_Y) < BARRIER_HALF_Y;
  }
  /** Phase 2: fade barrier mesh opacity as HP drops; hide at 0. */
  private updateBarrierVisual(idx: number) {
    const m = this.barrierMeshes[idx];
    if (!m) return;
    const hp = this.barrierHp[idx];
    const mat = m.material as THREE.MeshStandardMaterial;
    if (hp <= 0) { m.visible = false; return; }
    mat.opacity = hp / BARRIER_HP;
  }

  private aliveCount(): number {
    let n = 0;
    for (let i = 0; i < this.aliveFlags.length; i++) if (this.aliveFlags[i]) n += 1;
    return n;
  }

  private alienWorldPos(idx: number): { x: number; y: number } {
    const r = Math.floor(idx / FORMATION_COLS);
    const c = idx % FORMATION_COLS;
    const cx = (c - (FORMATION_COLS - 1) / 2) * FORMATION_X_SPACING;
    const cy = FORMATION_TOP_Y - r * FORMATION_Y_SPACING;
    return { x: cx + this.fx, y: cy + this.fy };
  }

  private stepFormation() {
    const nextFx = this.fx + this.dir * SHIFT;
    let exitL = false, exitR = false;
    for (let i = 0; i < this.aliveFlags.length; i++) {
      if (!this.aliveFlags[i]) continue;
      const c = i % FORMATION_COLS;
      const cx = (c - (FORMATION_COLS - 1) / 2) * FORMATION_X_SPACING + nextFx;
      if (cx > ARENA_HALF_X - 1) exitR = true;
      if (cx < -ARENA_HALF_X + 1) exitL = true;
    }
    if ((this.dir === 1 && exitR) || (this.dir === -1 && exitL)) {
      this.dir = (this.dir === 1 ? -1 : 1) as 1 | -1;
      this.fy -= DROP;
    } else {
      this.fx = nextFx;
    }
    // Sync formation group position visually.
    this.formationGroup.position.set(this.fx, this.fy, 0);
  }

  private currentStepInterval(): number {
    const now = this.aliveCount();
    if (this.initialAlive <= 1) return STEP_INTERVAL_MS_MIN;
    const f = (this.initialAlive - now) / (this.initialAlive - 1);
    return STEP_INTERVAL_MS_BASE + (STEP_INTERVAL_MS_MIN - STEP_INTERVAL_MS_BASE) * f;
  }

  private fireBombFrom(rng: () => number): { x: number; y: number } | null {
    const startCol = Math.floor(rng() * FORMATION_COLS);
    for (let attempt = 0; attempt < FORMATION_COLS; attempt++) {
      const c = (startCol + attempt) % FORMATION_COLS;
      for (let r = 0; r < FORMATION_ROWS; r++) {
        const i = r * FORMATION_COLS + c;
        if (this.aliveFlags[i]) return this.alienWorldPos(i);
      }
    }
    return null;
  }

  private recordSample() {
    const steer: -1 | 0 | 1 = this.inLeft ? -1 : this.inRight ? 1 : 0;
    const fire: 0 | 1 = this.fireQueued ? 1 : 0;
    const t = Math.round(performance.now() - this.startedAt);
    if (this.inputs.length < MAX_INPUT_SAMPLES) this.inputs.push({ t, steer, fire });
  }

  private frame(dt: number) {
    if (this.gameOver) return;
    const now = performance.now();
    const runMs = now - this.startedAt;
    if (runMs >= RUN_MS) { this.endRun(runMs, false); return; }

    // Boost intensity: how much of the formation you've cleared. As the
    // last few aliens dash around fast, the screen heats up — pumping
    // FOV + bloom matches the canon "tempo speeds up" feel.
    // (depth-bar post-processing)
    const cleared = 1 - this.aliveCount() / this.initialAlive;
    this.engine.setBoost(cleared, dt);

    // v6 — adaptive audio drone tracks the same `cleared` ratio so the
    // bed thickens audibly as the formation empties. Per memory
    // feedback-sibling-games-shallow §"at least one escalation hook".
    // Cap at 0.7 — full intensity is reserved for moments where the
    // game-over chord lands (handled elsewhere if at all). The setter
    // damps internally; setting per-frame is cheap.
    try { this.audio.setDroneIntensity(0.15 + cleared * 0.55); } catch { /* off */ }

    // Touch steering: convert touchTargetX → inLeft/inRight before any
    // sampling or movement so the recorded input matches what the player
    // sees the ship doing.
    if (this.touchTargetX !== null) {
      const diff = this.touchTargetX - this.sx;
      const deadzone = 0.15;
      if (Math.abs(diff) < deadzone) {
        this.inLeft = false;
        this.inRight = false;
      } else if (diff > 0) {
        this.inRight = true;
        this.inLeft = false;
      } else {
        this.inLeft = true;
        this.inRight = false;
      }
    }

    this.sampleTimer += dt * 1000;
    if (this.sampleTimer >= SAMPLE_INTERVAL_MS) {
      this.sampleTimer = 0;
      this.recordSample();
    }

    // Ship.
    const steer = this.inLeft ? -1 : this.inRight ? 1 : 0;
    this.sx += steer * SHIP_MAX_SPEED * dt;
    if (this.sx > ARENA_HALF_X - 0.7) this.sx = ARENA_HALF_X - 0.7;
    if (this.sx < -ARENA_HALF_X + 0.7) this.sx = -ARENA_HALF_X + 0.7;
    this.shipMesh.position.x = this.sx;

    if (this.fireQueued) {
      this.fireQueued = false;
      if (runMs - this.lastFireT >= FIRE_COOLDOWN_MS) {
        this.lastFireT = runMs;
        const m = new THREE.Mesh(this.upGeo, this.upMat);
        m.position.set(this.sx, SHIP_Y + 0.6, 0);
        this.engine.scene.add(m);
        this.ups.push({ x: this.sx, y: SHIP_Y + 0.6, spawn_t: runMs, mesh: m });
        try { this.audio.chime(60); } catch { /* off */ }
      }
    }

    // Step formation if interval elapsed.
    if (this.aliveCount() > 0) {
      const stepMs = this.currentStepInterval();
      while (runMs - this.lastStepT >= stepMs) {
        this.lastStepT += stepMs;
        this.stepFormation();
        // v2(b) escalation: per-tick bomb prob ramps over the run.
        const tickFrac = Math.min(1, this.lastStepT / RUN_MS);
        const tickProb = BOMB_FIRE_PROB_PER_TICK +
          (BOMB_FIRE_PROB_END - BOMB_FIRE_PROB_PER_TICK) * tickFrac;
        if (this.rng() < tickProb * this.aliveCount()) {
          const src = this.fireBombFrom(this.rng);
          if (src) {
            const m = new THREE.Mesh(this.bombGeo, this.bombMat);
            m.position.set(src.x, src.y, 0);
            this.engine.scene.add(m);
            this.bombs.push({ x: src.x, y: src.y, spawn_t: this.lastStepT, mesh: m });
          }
        }
      }
    }

    // Move bombs (Phase 2: check barrier consume before despawn check).
    for (let i = this.bombs.length - 1; i >= 0; i--) {
      const b = this.bombs[i];
      b.y -= BOMB_SPEED * dt;
      b.mesh.position.set(b.x, b.y, 0);
      let consumed = false;
      for (let bi = 0; bi < BARRIER_COUNT; bi++) {
        if (this.barrierHp[bi] > 0 && this.barrierHits(b.x, b.y, bi)) {
          this.barrierHp[bi] -= 1;
          this.updateBarrierVisual(bi);
          consumed = true;
          break;
        }
      }
      if (consumed) {
        this.engine.scene.remove(b.mesh);
        this.bombs.splice(i, 1);
        continue;
      }
      if (b.y < SHIP_Y - 4 || runMs - b.spawn_t > PROJ_TTL_MS * 2) {
        this.engine.scene.remove(b.mesh);
        this.bombs.splice(i, 1);
      }
    }

    // Move up-projectiles (Phase 2: barrier consume here too).
    for (let i = this.ups.length - 1; i >= 0; i--) {
      const u = this.ups[i];
      u.y += UP_PROJECTILE_SPEED * dt;
      u.mesh.position.set(u.x, u.y, 0);
      let consumed = false;
      for (let bi = 0; bi < BARRIER_COUNT; bi++) {
        if (this.barrierHp[bi] > 0 && this.barrierHits(u.x, u.y, bi)) {
          this.barrierHp[bi] -= 1;
          this.updateBarrierVisual(bi);
          consumed = true;
          break;
        }
      }
      if (consumed) {
        this.engine.scene.remove(u.mesh);
        this.ups.splice(i, 1);
        continue;
      }
      if (u.y > FORMATION_TOP_Y + 5 || runMs - u.spawn_t > PROJ_TTL_MS) {
        this.engine.scene.remove(u.mesh);
        this.ups.splice(i, 1);
      }
    }

    // v5: UFO/saucer — spawn mesh if active, animate, check collision.
    const saucer = this.saucerAt(runMs);
    if (saucer && !this.killedSaucerTs.has(saucer.spawn_t)) {
      if (this.saucerSpawnT !== saucer.spawn_t) {
        // New saucer — spawn its mesh.
        if (this.saucerMesh) this.engine.scene.remove(this.saucerMesh);
        const geo = new THREE.TorusGeometry(0.9, 0.3, 6, 18);
        const mat = new THREE.MeshStandardMaterial({
          color: 0xa6ff5b, emissive: 0xa6ff5b, emissiveIntensity: 0.9,
          roughness: 0.3, metalness: 0.5,
        });
        this.saucerMesh = new THREE.Mesh(geo, mat);
        this.engine.scene.add(this.saucerMesh);
        this.saucerSpawnT = saucer.spawn_t;
      }
      if (this.saucerMesh) {
        this.saucerMesh.position.set(saucer.x, UFO_Y, 0);
        this.saucerMesh.rotation.z += dt * 3;
      }
      // Up-projectile × saucer.
      for (let i = this.ups.length - 1; i >= 0; i--) {
        const u = this.ups[i];
        if (Math.hypot(u.x - saucer.x, u.y - UFO_Y) < UFO_HIT_RADIUS) {
          this.killedSaucerTs.add(saucer.spawn_t);
          this.saucerHits += 1;
          this.scoreEstimate += UFO_BONUS;
          if (runMs - saucer.spawn_t <= UFO_PERFECT_MS) {
            this.saucerPerfects += 1;
            this.scoreEstimate += UFO_PERFECT_BONUS;
            try { this.audio.chime(280); } catch { /* off */ }
          } else {
            try { this.audio.chime(180); } catch { /* off */ }
          }
          this.engine.scene.remove(u.mesh);
          this.ups.splice(i, 1);
          if (this.saucerMesh) {
            this.engine.scene.remove(this.saucerMesh);
            this.saucerMesh = null;
          }
          this.saucerSpawnT = -1;
          break;
        }
      }
    } else if (this.saucerMesh) {
      // Saucer expired (crossed off-screen) or already-killed and we
      // shouldn't be holding its mesh; clean up.
      this.engine.scene.remove(this.saucerMesh);
      this.saucerMesh = null;
      this.saucerSpawnT = -1;
    }

    // Up × alien.
    for (let i = this.ups.length - 1; i >= 0; i--) {
      const u = this.ups[i];
      let hit = false;
      for (let r = 0; r < FORMATION_ROWS && !hit; r++) {
        for (let c = 0; c < FORMATION_COLS; c++) {
          const idx = r * FORMATION_COLS + c;
          if (!this.aliveFlags[idx]) continue;
          const pos = this.alienWorldPos(idx);
          if (Math.hypot(u.x - pos.x, u.y - pos.y) < HIT_RADIUS) {
            this.aliveFlags[idx] = 0;
            this.aliens[idx].mesh.visible = false;
            this.kills += 1;
            this.scoreEstimate += ROW_WEIGHT[r];
            this.engine.scene.remove(u.mesh);
            this.ups.splice(i, 1);
            try { this.audio.chime((r + 1) * 40); } catch { /* off */ }
            hit = true;
            break;
          }
        }
      }
    }

    // Bomb × ship.
    for (const b of this.bombs) {
      if (Math.abs(b.x - this.sx) < SHIP_HIT_RADIUS + BOMB_HIT_RADIUS &&
          Math.abs(b.y - SHIP_Y) < SHIP_HIT_RADIUS + BOMB_HIT_RADIUS) {
        this.endRun(runMs, true);
        return;
      }
    }

    // Wave clear.
    if (this.aliveCount() === 0 && this.wavesCleared === 0) {
      this.wavesCleared = 1;
      this.scoreEstimate += CLEAR_BONUS;
    }

    this.cb.onHud(runMs, this.kills, this.aliveCount(), this.scoreEstimate);
  }

  private endRun(runMs: number, died: boolean) {
    this.gameOver = true;
    // v6 — quiet the adaptive drone so it doesn't bleed into the
    // game-over screen. The base AudioBed has its own decay; this just
    // releases our hold on intensity.
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    let barriersRemaining = 0;
    for (const hp of this.barrierHp) if (hp > 0) barriersRemaining += 1;
    const result: PhalanxResult = {
      kills: this.kills,
      waves_cleared: this.wavesCleared,
      saucer_hits: this.saucerHits,
      saucer_perfects: this.saucerPerfects,
      barriers_remaining: barriersRemaining,
      died,
      duration_ms: Math.round(runMs),
      inputs: this.inputs,
      scoreEstimate: this.scoreEstimate,
    };
    this.cb.onGameOver(result);
  }

  start() {
    this.startedAt = performance.now();
    this.engine.start();
  }
  armAudio() { try { this.audio.arm(); } catch { /* gesture-gated */ } }
  setVolume(v: number) { this.audio.setVolume(v); }
  setMuted(m: boolean) { this.audio.setMuted(m); }
  dispose() {
    this.gameOver = true;
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    // v6 — drop the parallax-starfield layers off the scene so a
    // navigate-away → mount cycle doesn't accumulate THREE.Points.
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    this.engine.stop();
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.unmountTouch();
  }
}
